import { useContext, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import Slider from "react-slick";
import {
  MdChevronLeft,
  MdChevronRight,
  MdOutlineSpeed,
  MdOutlinePrecisionManufacturing,
  MdOutlineRecycling,
  MdOutlineSecurity,
  MdOutlineArchitecture,
} from "react-icons/md";
import lgs1 from "../assets/lgs1.jpeg";
import lgs2 from "../assets/lgs2.jpeg";
import lgs3 from "../assets/lgs3.jpeg";
import { AppContext } from "../App";

export default function Lgs() {
  const images = [lgs1, lgs2, lgs3];
  const { navTo } = useContext(AppContext);

  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: false,
      offset: 80,
    });
    AOS.refresh();
  }, []);

  const sliderSettings = {
    infinite: true,
    speed: 650,
    autoplay: true,
    autoplaySpeed: 3000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <MdChevronRight size={28} />,
    prevArrow: <MdChevronLeft size={28} />,
  };

  return (
    <section className="lgs-page py-5 mt-5">
      <div className="container">

        {/* HERO */}
        <header className="lgs-hero" data-aos="fade-up">
          <h1 className="text-primary">LIGHT GAUGE STEEL</h1>
          <p className="lgs-hero-sub font-semi-bold px-3">
            Build faster, Stronger, Smarter
          </p>
          <p className="text-muted">
            Light Gauge Steel provides a modern, efficient framework that keeps construction projects on schedule, reduces material waste and lowers labor costs. Its lightweight yet strong steel frames make installation faster and more predictable than traditional timber or concrete methods
          </p>
        </header>

        {/* MAIN GRID */}
        <div className="lgs-grid">

          {/* SLIDER CARD */}
          <div className="lgs-card p-2 p-md-3 lgs-slider" data-aos="fade-up">
            <Slider {...sliderSettings}>
              {images.map((img, i) => (
                <img key={i} src={img} alt={`LGS ${i + 1}`} />
              ))}
            </Slider>

            <p className="lgs-body">
              Steel frames resist pests, moisture, fire and rot, giving your building long-term reliability with minimal maintenance. Fully recyclable, they also support sustainable construction practices 
            </p>

            <button
              className="btn btn-primary btn-sm"
              onClick={() => navTo({ item: "request_quote" })}
            >
              Request Free Consultation
            </button>
          </div>

          {/* FEATURES */}
          <div className="lgs-stack" data-aos="fade-up">
            <div className="lgs-info-box rounded bg-light mShadow2  p-3 p-md-4">
              <p className="font-semi-bold">Versatile enough to fit every stage of construction, Light Gauge Steel adapts seamlessly to diverse building needs:</p>
              <ul className="lgs-usage-list">
                <li>Roof trusses and purlins</li>
                <li>Floor joists and ceilings</li>
                <li>External facades and cladding</li>
                <li>Custom or specialized structures</li>
                <li>Wall frames and interior partitions</li>
              </ul>
            </div>

            <div className="lgs-info-box bg-light rounded mShadow2  p-3 p-md-4">
              <h6 className="text-primary mb-3">Why Choose LGS</h6>
              <ul className="lgs-feature-list d-flex">
                <li data-aos="fade-right"><span className="lgs-feature-chip w-100 flex-shrink-0 p-1 rounded border"><MdOutlineSpeed className="text-primary me-1" size={24} />High strength-to-weight ratio</span></li>
                <li data-aos="fade-right"><span className="lgs-feature-chip w-100 flex-shrink-0 p-1 rounded border"><MdOutlineSpeed className="text-primary me-1" size={24} />Faster builds predictable timelines</span></li>
                <li data-aos="fade-right"><span className="lgs-feature-chip w-100 flex-shrink-0 p-1 rounded border"><MdOutlinePrecisionManufacturing className="text-primary me-1" size={24} />Precision-engineered components for minimal onsite errors</span></li>
                <li data-aos="fade-right"><span className="lgs-feature-chip w-100 flex-shrink-0 p-1 rounded border"><MdOutlineSecurity className="text-primary me-1" size={24} />Adaptable to complex or customized layouts</span></li>
                <li data-aos="fade-right"><span className="lgs-feature-chip w-100 flex-shrink-0 p-1 rounded border"><MdOutlineRecycling className="text-primary me-1" size={24} />Corrosion-resistant and durable/long lasting</span></li>
                <li data-aos="fade-right"><span className="lgs-feature-chip w-100 flex-shrink-0 p-1 rounded border"><MdOutlineArchitecture className="text-primary me-1" size={24} />Low-maintenance</span></li>
              </ul>
            </div>
          </div>

        </div>

        {/* APPLICATIONS */}
        <div className="text-center my-5" data-aos="fade-up">
          <p className="text-muted">
            With Light Gauge Steel, projects are completed faster, built stronger and designed smarter: <br/><br/>
            <b>A practical solution for modern construction challenges.</b>
          </p>
        </div>

        <div className="bg-surface p-2 rounded mb-5 text-center py-3" data-aos="fade-up">
          <h6 className="text-primary fs-3 font-bold" style={{color:"var(--primaryColor) !important"}}>Ideal for Extensions, Modular Builds and Remote Sites</h6>
          <p className="text-light">LGS adapts seamlessly to diverse environments, from urban expansions to eco-sensitive lodges </p>
        </div>

        {/* USE CASES */}
        <div className="lgs-grid">

          <div className="lgs-card p-2 p-md-3" data-aos="fade-up">
            <p className="lgs-body">
              Highly adaptable, LGS is ideal for vertical and horizontal extensions, modular builds and prefabricated structures, allowing seamless integration with existing buildings and enabling custom layouts and expansions without compromising structural integrity.
            </p>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navTo({ item: "request_quote" })}
            >
              Request Consultation
            </button>
          </div>

          <div className="lgs-card p-2 p-md-3" data-aos="fade-up">
            <p className="lgs-body">
              Its lightweight components and off-site fabrication make it particularly effective in remote or environmentally sensitive locations such as game reserves, national parks and protected sites, where eco-lodges, elevated platforms, modular units and support facilities can be delivered efficiently with reduced environmental impact.
            </p>
          </div>

        </div>

      </div>

      {/* STYLES */}
      <style>{`
        .lgs-page {
          background: radial-gradient(circle at top right, rgba(15, 118, 110, 0.08), transparent 45%),
            #f8fafc;
          font-family: Inter, system-ui, sans-serif;
        }

        .lgs-hero {
          text-align: center;
          margin: 0 auto 64px;
        }

        .lgs-kicker {
          font-size: 13px;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: #6b7280;
        }

        h1 {
          font-size: 48px;
          margin: 12px 0;
        }

        .lgs-hero-sub {
          font-size: 18px;
          color: #4b5563;
        }

        .lgs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 32px;
          align-items: stretch;
        }

        .lgs-card {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
          border: 1px solid rgba(148, 163, 184, 0.18);
          display: flex;
          flex-direction: column;
          gap: 24px;
          height: 100%;
        }

        .lgs-grid > * {
          height: 100%;
        }

        .lgs-stack {
          display: flex;
          flex-direction: column;
          gap: 24px;
          height: 100%;
        }

        .lgs-info-box {
          background: #fff;
          border-radius: 12px;
          border: 1px solid rgba(148, 163, 184, 0.14);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
        }

        .lgs-slider img {
          width: 100%;
          border-radius: 16px;
          object-fit: cover;
          border: 1px solid rgba(148, 163, 184, 0.2);
        }

        .lgs-body {
          color: #374151;
          line-height: 1.7;
        }

        .btn-primary {
          align-self: flex-start;
          padding: 12px 24px;
          border-radius: 999px;
          background: #0f766e;
          color: #fff;
          border: none;
          font-weight: 600;
          cursor: pointer;
        }

        .lgs-feature-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .lgs-feature-list li {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 15px;
        }

        .lgs-feature-chip {
          background: rgba(15, 23, 42, 0.04);
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .lgs-usage-list {
          list-style: none;
          padding: 0;
          margin: 8px 0 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
          color: #374151;
        }

        .lgs-usage-list li {
          position: relative;
          padding-left: 16px;
        }

        .lgs-usage-list li::before {
          content: "-";
          position: absolute;
          left: 0;
          color: #0f766e;
          font-weight: 700;
        }

        .lgs-page .text-primary {
          color: #0f172a !important;
        }

        .lgs-page .text-muted {
          color: #475569 !important;
        }

        .lgs-section {
          text-align: center;
          margin-bottom: 64px;
        }

        .lgs-pill-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 12px;
          margin-top: 24px;
        }

        .lgs-pill-grid span {
          padding: 10px 18px;
          background: #e5f7f5;
          border-radius: 999px;
          font-size: 14px;
        }

        .lgs-footnote {
          text-align: center;
          color: #6b7280;
          max-width: 680px;
          margin: 0 auto;
        }
      `}</style>
    </section>
  );
}
